import { isReviewRequestLookupCommand } from './workflow-review-request-lookup-authoring-gate';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  isUnsupportedFormConfiguration,
  hasInstalledProvidedLookupRuntime,
  hasInstalledReviewRequestLookupRuntime,
  hasInstalledPreparationLookupRuntime,
  workflowFormFieldIssues,
} from './workflow-form-fields-validation';
import type {
  WorkflowFormConfiguration,
  WorkflowFormFieldConfiguration,
  WorkflowFormOption,
} from '@app/shared/workflow-commands/workflow-form-configuration';
import type {
  StepAuthoringCommand,
  StepAuthoringFormField,
  StepCommand,
} from './workflow-step-composition';
import type { WorkflowFormLayout } from '@app/shared/workflow-commands/workflow-form-layout';
import {
  workflowFormLayoutIssue,
  workflowFormWidthChoices,
} from './workflow-form-layout-authoring';
import {
  providedFormAuthoringContextReason,
  providedFormSettingsAuthoringReason,
  providedFormSettingsShapeReason,
  preparationLookupSettingsAuthoringReason,
  reviewRequestLookupSettingsAuthoringReason,
  preparationLookupHandoffReason,
  providedLookupAuthoringReason,
  providedLookupSourceReason,
  type ProvidedFormAuthoringContext,
} from './workflow-provided-lookup-authoring';
import { isPreparationLookupCommand } from './workflow-preparation-lookup-authoring-gate';

@Component({
  selector: 'phms-workflow-form-fields-editor',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    DragDropModule,
  ],
  templateUrl: './workflow-form-fields-editor.component.html',
  styleUrl: './workflow-form-fields-editor.component.scss',
})
export class WorkflowFormFieldsEditorComponent implements OnChanges {
  @Input({ required: true }) command!: StepCommand;
  @Input({ required: true }) installed!: StepAuthoringCommand;
  @Input() disabled = false;
  @Input() inspectorMode = false;
  @Input() selectedFieldId: string | null = null;
  /** Enabled by the document owner only after the explicit compatible upgrade. */
  @Input() layoutEnabled = false;
  @Input() managedLayout = false;
  /** Live document ownership is supplied by the parent; absent context keeps v3 inspection read-only. */
  @Input() authoringContext: (() => ProvidedFormAuthoringContext | null) | null = null;
  @Input() editAvailable: (() => boolean) | null = null;
  @Input() activeField: (() => string | null) | null = null;
  @Input() lookupSourceAvailable = false;
  @Input() lookupSourceReason = '';
  readonly widthChoices = workflowFormWidthChoices;
  private authoringOwner: string | null = null;
  private authoringRegistered = false;
  private restoringModel = false;
  error = '';
  ngOnChanges(changes: SimpleChanges = {}): void {
    this.error = '';
    if (changes['command'] || !this.authoringRegistered && this.authoringContext) {
      const context = this.authoringContext?.();
      if (context) { this.authoringOwner = context.ownerKey; this.authoringRegistered = true; }
    }
  }
  @Output() fieldSelectionChange = new EventEmitter<string | null>();
  @Output() layoutChangeRequested = new EventEmitter<{ fieldId: string; change: Partial<WorkflowFormLayout> }>();
  @Output() configurationChange =
    new EventEmitter<WorkflowFormConfiguration | null>();
  @Output() lookupSourceRequested = new EventEmitter<string>();
  @Output() inlineSourceRequested = new EventEmitter<string>();
  get fields() {
    return this.installed.formFields ?? [];
  }
  get unsupported() {
    return isUnsupportedFormConfiguration(this.command);
  }
  get readerOnly(): boolean {
    return !!this.authoringUnavailableReason || this.command.formConfiguration?.version === 3
      && !!providedFormSettingsAuthoringReason(this.currentAuthoringContext, this.command, this.installed);
  }
  get authoringUnavailableReason(): string { return reviewRequestLookupSettingsAuthoringReason(this.currentAuthoringContext, this.command, this.installed) || preparationLookupSettingsAuthoringReason(this.currentAuthoringContext, this.command, this.installed); }
  private get fieldScopeRequired(): boolean { return this.command.formConfiguration?.version === 3 || isPreparationLookupCommand(this.command.capability) || isReviewRequestLookupCommand(this.command.capability); }
  private get currentAuthoringContext(): ProvidedFormAuthoringContext | null {
    const context = this.authoringContext?.();
    return context && this.authoringRegistered && context.ownerKey === this.authoringOwner ? context : null;
  }
  get unavailable(): boolean { return this.disabled || this.editAvailable?.() === false; }
  private fieldCurrent(field: StepAuthoringFormField): boolean {
    const selected = this.activeField ? this.activeField() : this.inspectorMode ? this.selectedFieldId : field.name;
    return this.fields.includes(field) && selected === field.name;
  }
  private restoreModel(model: NgModel | undefined, value: unknown): void {
    if (!model) return;
    this.restoringModel = true;
    try {
      // Keep NgModel's public model/view cache aligned without treating restoration as a new edit.
      model.control.setValue(value, { emitEvent: false });
    } finally { this.restoringModel = false; }
  }
  get lookupRuntimeAvailable(): boolean {
    const validation = this.validation;
    return (hasInstalledProvidedLookupRuntime(this.command, this.installed) || hasInstalledPreparationLookupRuntime(this.command, this.installed) || hasInstalledReviewRequestLookupRuntime(this.command, this.installed))
      && !validation.form && !Object.keys(validation.fields).length;
  }
  get visibleFields() {
    return this.inspectorMode
      ? this.fields.filter((field) => field.name === this.selectedFieldId)
      : this.fields;
  }
  get invalidFields() {
    return this.fields.filter((field) => this.issue(field));
  }
  fieldLabel(field: StepAuthoringFormField) {
    return this.settings(field).label?.trim() || field.label;
  }
  selectField(id: string): void {
    if (!this.fields.some((field) => field.name === id)) return;
    this.selectedFieldId = id;
    this.fieldSelectionChange.emit(id);
  }
  private get savedFields() {
    return this.unsupported
      ? []
      : (this.command.formConfiguration?.fields ?? []);
  }
  get unavailableFields() {
    return this.savedFields.filter(
      (field) => !this.fields.some((known) => known.name === field.name),
    );
  }
  settings(field: StepAuthoringFormField): WorkflowFormFieldConfiguration {
    return (
      this.savedFields.find((item) => item.name === field.name) ?? {
        name: field.name,
      }
    );
  }
  control(field: StepAuthoringFormField) {
    return this.settings(field).control ?? field.control;
  }
  source(field: StepAuthoringFormField) {
    return (
      this.settings(field).optionSource ??
      (this.changed(field)
        ? 'contract-options'
        : field.optionSources.includes('step-outcomes')
          ? 'step-outcomes'
          : 'contract-options')
    );
  }
  sourceLabel(source: string) {
    return (
      (
        {
          'contract-options': 'Action choices',
          'step-outcomes': 'This step’s outcomes',
          'custom-options': 'Choices defined here',
          lookup: 'Lookup choices',
        } as Record<string, string>
      )[source] ?? source
    );
  }
  options(field: StepAuthoringFormField): readonly WorkflowFormOption[] {
    if (this.source(field) === 'lookup') return [];
    if (this.source(field) === 'custom-options')
      return this.settings(field).options ?? [];
    if (this.source(field) === 'step-outcomes')
      return this.command.outcomes.map((outcome) => ({
        value: outcome.code,
        label: outcome.label,
      }));
    return field.options;
  }
  private emit(fields: WorkflowFormFieldConfiguration[]) {
    if (this.unavailable || this.unsupported || this.readerOnly) return;
    const layoutIssue = this.layoutIssue(fields);
    if (layoutIssue) {
      this.error = layoutIssue;
      return;
    }
    const configuration =
      fields.length || [2, 3].includes(this.command.formConfiguration?.version ?? 1)
        ? {
            ...this.command.formConfiguration,
            version: this.command.formConfiguration?.version ?? 1,
            fields,
          }
        : null;
    const command = { ...this.command, formConfiguration: configuration };
    if (configuration?.version === 3 || isPreparationLookupCommand(command.capability) || isReviewRequestLookupCommand(command.capability)) {
      const context = this.currentAuthoringContext;
      const reason = context && (isPreparationLookupCommand(command.capability) || isReviewRequestLookupCommand(command.capability)
        ? providedFormSettingsShapeReason(context.document, command, this.installed) : providedLookupAuthoringReason(context.document, command, this.installed));
      if (!context || reason) { this.error = reason || 'The selected form changed. Reopen its current settings before editing.'; return; }
    }
    this.command = command;
    this.error = '';
    this.configurationChange.emit(configuration);
  }
  update(
    field: StepAuthoringFormField,
    change: Partial<WorkflowFormFieldConfiguration>,
    model?: NgModel,
  ) {
    if (this.restoringModel) return;
    const restore = () => {
      if (!model) return;
      const property = Object.keys(change)[0] as keyof WorkflowFormFieldConfiguration;
      const setting = this.settings(field);
      const value = property === 'label' ? setting.label ?? field.label : property === 'required' ? setting.required ?? field.required
        : property === 'maxLength' ? setting.maxLength ?? null : setting[property];
      this.restoreModel(model, value);
    };
    if (this.unavailable || this.unsupported || this.readerOnly || !this.fields.includes(field)
      || this.fieldScopeRequired && !this.fieldCurrent(field)) { restore(); return; }
    if (Object.hasOwn(change, 'layout') && !this.canEditLayout(field)) { restore(); return; }
    if (this.command.formConfiguration?.version === 3 && (Object.hasOwn(change, 'lookup')
      || change.optionSource === 'lookup' || this.source(field) === 'lookup' && ['control', 'optionSource', 'options'].some(key => Object.hasOwn(change, key)))) { restore(); return; }
    const settings = { ...this.settings(field), ...change };
    if (
      field.control === 'select' &&
      !this.changed(field) &&
      !settings.optionSource
    )
      settings.optionSource = this.source(
        field,
      ) as WorkflowFormFieldConfiguration['optionSource'];
    if (field.required && settings.required === false) { restore(); return; }
    // Clearing a property restores the installed default; unrelated fields stay intact.
    const next = Object.fromEntries(
      Object.entries(settings).filter(([, value]) => value !== undefined),
    ) as unknown as WorkflowFormFieldConfiguration;
    const fields = [...(this.command.formConfiguration?.fields ?? [])];
    const index = fields.findIndex((item) => item.name === field.name);
    if (index < 0) fields.push(next);
    else fields[index] = next;
    this.emit(fields);
    if (this.settings(field) !== next && JSON.stringify(this.settings(field)) !== JSON.stringify(next)) restore();
  }
  canEditLayout(field: StepAuthoringFormField): boolean {
    return (
      this.layoutEnabled &&
      !this.unavailable &&
      !this.unsupported &&
      !this.readerOnly &&
      ([2, 3].includes(this.command.formConfiguration?.version ?? 1) || this.managedLayout && (this.command.formConfiguration?.version ?? 1) === 1) &&
      this.fields.includes(field) &&
      (!this.fieldScopeRequired || this.fieldCurrent(field)) &&
      field.supportsLayout === true
    );
  }
  private layoutIssue(settings: WorkflowFormFieldConfiguration[]): string {
    if (
      settings.some(
        (setting) =>
          setting.layout != null &&
          this.fields.find((field) => field.name === setting.name)
            ?.supportsLayout !== true,
      )
    )
      return 'This action does not permit layout changes for the selected field.';
    return workflowFormLayoutIssue(
      this.managedLayout && (this.command.formConfiguration?.version ?? 1) === 1 ? 2 : this.command.formConfiguration?.version ?? 1,
      this.fields.map((field) => {
        const setting = settings.find((item) => item.name === field.name);
        return {
          control: setting?.control ?? field.control,
          layout: setting?.layout,
        };
      }),
    );
  }
  layoutChoiceReason(
    field: StepAuthoringFormField,
    change: Partial<WorkflowFormLayout>,
  ): string {
    if (this.unavailable) return 'This form is read-only.';
    if (this.readerOnly) return 'This saved provided-form configuration is read-only in this editor. Its settings are preserved.';
    if (field.supportsLayout !== true)
      return 'Field layout is fixed by this action.';
    if (!this.canEditLayout(field))
      return 'Field sizing is unavailable for this saved form.';
    const current = this.settings(field),
      next = { ...current, layout: { ...current.layout, ...change } };
    const settings = this.savedFields.filter(
      (item) => item.name !== field.name,
    );
    return this.layoutIssue([...settings, next]);
  }
  setLayout(
    field: StepAuthoringFormField,
    change: Partial<WorkflowFormLayout>,
    model?: NgModel,
  ): void {
    if (this.restoringModel) return;
    const restore = () => this.restoreModel(model, Object.hasOwn(change, 'width') ? this.settings(field).layout?.width ?? 'fill'
      : this.settings(field).layout?.row ?? 'new');
    const reason = this.layoutChoiceReason(field, change);
    if (reason) {
      this.error = reason;
      restore();
      return;
    }
    if (this.managedLayout) {
      if (this.unavailable || this.unsupported || this.readerOnly || !this.fields.includes(field)
        || this.fieldScopeRequired && !this.fieldCurrent(field)) { restore(); return; }
      this.layoutChangeRequested.emit({ fieldId: field.name, change });
      restore();
      return;
    }
    this.update(field, {
      layout: { ...this.settings(field).layout, ...change },
    });
    if (this.error) restore();
  }
  setControl(
    field: StepAuthoringFormField,
    control: 'text' | 'textarea' | 'select',
    model?: NgModel,
  ) {
    if (this.restoringModel) return;
    const restore = () => this.restoreModel(model, this.control(field));
    if (this.unavailable || this.readerOnly || this.source(field) === 'lookup') { restore(); return; }
    if (field.control === 'select' && control !== 'select') { restore(); return; }
    if (control === 'select') {
      if (!this.editableSources(field).length) { restore(); return; }
      const optionSource = field.optionSources.includes('step-outcomes')
        ? 'step-outcomes'
        : field.optionSources.includes('contract-options')
          ? 'contract-options'
          : 'custom-options';
      this.update(field, {
        control,
        optionSource,
        options:
          optionSource === 'custom-options'
            ? (this.settings(field).options ?? [])
            : undefined,
      });
    } else
      this.update(field, {
        control,
        optionSource: undefined,
        options: undefined,
      });
    if (this.control(field) !== control) restore();
  }
  setSource(field: StepAuthoringFormField, source: string, model?: NgModel) {
    if (this.restoringModel) return;
    if (source === 'lookup') { this.requestLookupSource(field); this.restoreModel(model, this.source(field)); return; }
    if (this.source(field) === 'lookup') {
      if (source === 'custom-options' && this.canRequestLookup(field)) this.inlineSourceRequested.emit(field.name);
      this.restoreModel(model, this.source(field)); return;
    }
    if (!this.editableSources(field).includes(source)) { this.restoreModel(model, this.source(field)); return; }
    this.update(field, {
      optionSource: source as WorkflowFormFieldConfiguration['optionSource'],
      options:
        source === 'custom-options'
          ? (this.settings(field).options ?? [])
          : undefined,
    });
    if (this.source(field) !== source) this.restoreModel(model, this.source(field));
  }
  canRequestLookup(field: StepAuthoringFormField): boolean {
    const context = this.currentAuthoringContext;
    return !this.unavailable && !this.unsupported && this.lookupSourceAvailable && !!context && this.fieldCurrent(field)
      && !providedFormAuthoringContextReason(context, this.command, this.installed)
      && !providedLookupAuthoringReason(context.document, this.command, this.installed)
      && !providedLookupSourceReason(this.command, this.installed, field.name);
  }
  lookupPermitted(field: StepAuthoringFormField): boolean {
    return !providedLookupSourceReason(this.command, this.installed, field.name);
  }
  requestLookupSource(field: StepAuthoringFormField): void {
    if (this.canRequestLookup(field)) this.lookupSourceRequested.emit(field.name);
  }
  moveOption(
    field: StepAuthoringFormField,
    index: number,
    offset: number,
  ): void {
    if (![-1, 1].includes(offset)) return;
    this.reorderOption(field, index, index + offset);
  }
  dropOption(
    field: StepAuthoringFormField,
    event: Pick<CdkDragDrop<unknown>, 'previousIndex' | 'currentIndex'>,
  ): void {
    this.reorderOption(field, event.previousIndex, event.currentIndex);
  }
  private reorderOption(
    field: StepAuthoringFormField,
    from: number,
    to: number,
  ): void {
    if (
      this.unavailable ||
      this.unsupported ||
      this.readerOnly ||
      this.fieldScopeRequired && !this.fieldCurrent(field) ||
      this.source(field) !== 'custom-options'
    )
      return;
    const options = [...this.options(field)];
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      from < 0 ||
      to < 0 ||
      from >= options.length ||
      to >= options.length ||
      from === to
    )
      return;
    options.splice(to, 0, options.splice(from, 1)[0]);
    this.update(field, { options });
  }
  updateOption(
    field: StepAuthoringFormField,
    index: number,
    change: Partial<WorkflowFormOption>,
  ) {
    this.update(field, {
      options: this.options(field).map((option, at) =>
        at === index ? { ...option, ...change } : option,
      ),
    });
  }
  addOption(field: StepAuthoringFormField) {
    this.update(field, {
      options: [...this.options(field), { value: '', label: '' }],
    });
  }
  removeOption(field: StepAuthoringFormField, index: number) {
    this.update(field, {
      options: this.options(field).filter((_, at) => at !== index),
    });
  }
  reset(field: StepAuthoringFormField) {
    if (this.fieldScopeRequired && !this.fieldCurrent(field)) return;
    this.emit(this.savedFields.filter((item) => item.name !== field.name));
  }
  changed(field: StepAuthoringFormField) {
    return this.savedFields.some((item) => item.name === field.name);
  }
  editableSources(field: StepAuthoringFormField): string[] {
    return field.optionSources.filter(source => ['contract-options', 'step-outcomes', 'custom-options'].includes(source));
  }
  get validation() {
    const issues = workflowFormFieldIssues(this.command, this.installed), context = this.currentAuthoringContext;
    const handoff = context && !issues.form ? preparationLookupHandoffReason(context.document, context.nodeId, this.command) : '';
    return handoff && !issues.fields['workSummary'] ? { ...issues, fields: { ...issues.fields, workSummary: handoff } } : issues;
  }
  issue(field: StepAuthoringFormField): string {
    return this.validation.fields[field.name] ?? '';
  }
  trackField(_: number, field: StepAuthoringFormField) {
    return field.name;
  }
  trackOption(index: number) {
    return index;
  }
}
